import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { NavLink, Redirect, useLocation } from 'react-router-dom'
import { BreadcrumbView } from '../../../context/breadcrumb.context'
import { useAuthContext } from '../../../context/auth.context'
import { CardFullHeightScrollY } from '../../../shared/card/card-full-height-scroll-y'
import { NotFoundPage } from '../../../page/not-found'
import { claimsKnown, lmsAdminVisible } from '../../lms-admin/lms-admin-gate'
import { useLmsAccess } from '../../lms-admin/lms-access'
import { ExportMenu } from './export-menu'
import { FilterBar } from './filter-bar'
import { LMS_INSIGHT_BASE, LMS_INSIGHT_TABS } from './lms-insight-tabs'
import { FALLBACK_TIME_ZONE, MAX_RANGE_DAYS, dayCount, defaultFilters, effectiveInterval, rangeProblem } from './lms-insight-query'
import { LmsInsightsUnavailable } from './report-status'
import { useInsightOptions } from './use-insight-options'
import { SECTION } from '../../../shared/navigation/sections'
import '../../../shared/sso-workspace/sso-workspace.scss'
import '../../../shared/sso-workspace/fleet.scss'

// The LMS insights pages (Analytics > LMS): how the organization's LMS
// connections are used. One filter bar (range, interval, LMS connection,
// course, the browser's time zone) and one export menu serve every tab; a tab
// is a data entry (lms-insight-tabs.js).
//
// While the server says the LMS is staff-only (lms-access.js) the pages exist
// only for system administrators and root: for anyone else every address under
// /org/analytic/lms is the not-found page an unknown address gets. Until the
// session's claims arrive nothing is drawn, so a system administrator never
// sees a not-found flash. The server's answer is never waited for.
export const LmsInsights = () => {
  const { state } = useAuthContext()
  const { pathname } = useLocation()
  useLmsAccess()

  if (!lmsAdminVisible(state)) return claimsKnown(state) ? <NotFoundPage pathname={pathname} /> : null

  return <LmsInsightsPage pathname={pathname} />
}

const LmsInsightsPage = ({ pathname }) => {
  const [chosen, updateChosen] = useState(() => defaultFilters())
  const [refreshKey, updateRefreshKey] = useState(0)

  const options = useInsightOptions({ tz: chosen.tz, connection: chosen.connection, refreshKey })

  // PostgreSQL may not know the zone the browser names, and then every read
  // answers 400. The service says so on /details: read in UTC from then on,
  // and say so.
  const [zoneFallback, updateZoneFallback] = useState(false)
  useEffect(() => {
    if (!options.zoneRejected) return
    updateChosen(current => ({ ...current, tz: FALLBACK_TIME_ZONE }))
    updateZoneFallback(true)
  }, [options.zoneRejected])

  const handleChange = useCallback(change => updateChosen(current => ({ ...current, ...change })), [])
  const handleRefresh = useCallback(() => updateRefreshKey(count => count + 1), [])

  // The filters the reports are asked for: what was chosen, with the interval
  // the range allows.
  const days = dayCount(chosen.start, chosen.end)
  const maxRangeDays = options.maxRangeDays || MAX_RANGE_DAYS
  const problem = rangeProblem(chosen.start, chosen.end, maxRangeDays)
  const filters = useMemo(
    () => ({ ...chosen, interval: effectiveInterval(chosen.interval, days, options.intervals) }),
    [chosen, days, options.intervals]
  )

  const tab = LMS_INSIGHT_TABS.find(item => item.to === pathname)
  const exports = tab ? tab.exports : undefined

  // The card's header menu is a component; one made only when the filters
  // change keeps an open menu open while the page redraws.
  const HeaderMenu = useMemo(() => () => <ExportMenu exports={exports} filters={filters} />, [exports, filters])

  if (pathname.replace(/\/+$/, '') === LMS_INSIGHT_BASE) return <Redirect to={LMS_INSIGHT_TABS[0].to} />
  if (!tab) return <NotFoundPage pathname={pathname} />

  const Tab = tab.Component

  return (
    <BreadcrumbView Icon={SECTION.analyticsLms.Icon} text={SECTION.analyticsLms.text}>
      <CardFullHeightScrollY
        title="LMS insights"
        subtitle="How learners reach your courses through your LMS connections."
        HeaderMenu={problem || options.unavailable ? undefined : HeaderMenu}>
        <div className="sw-workspace pb-3" data-testid="lms-insights">
          {options.unavailable
            ? <LmsInsightsUnavailable />
            : <React.Fragment>
                <nav className="sw-tabs" aria-label="LMS insights">
                  {LMS_INSIGHT_TABS.map(item => <NavLink key={item.id} to={item.to}>{item.label}</NavLink>)}
                </nav>
                <FilterBar
                  chosen={chosen}
                  interval={filters.interval}
                  days={days}
                  intervals={options.intervals}
                  connections={options.connections}
                  courses={options.courses}
                  today={moment().format('YYYY-MM-DD')}
                  onChange={handleChange}
                  onRefresh={handleRefresh}
                />
                <p className="small text-body-secondary mb-3">
                  {zoneFallback
                    ? "Showing times in UTC: this browser's time zone isn't recognized."
                    : `Dates and times are in your time zone (${filters.tz}).`}
                </p>
                {problem
                  ? <div className="sw-notice warn" role="alert" data-testid="lms-insights-range-problem"><p>{problem}</p></div>
                  : <Tab key={tab.id} filters={filters} refreshKey={refreshKey} />}
              </React.Fragment>}
        </div>
      </CardFullHeightScrollY>
    </BreadcrumbView>
  )
}
